"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import React, { useRef } from "react"
import LeftPageButtonIcon from "../shared/LeftPageButtonIcon"
import RightPageButtonIcon from "../shared/RightPageButtonIcon"

// Native CSS scroll-snap carousel (replaced embla). No JS init and no forced
// reflows on mount — the only client work is a ref + two arrow handlers. Cards
// are rendered server-side by MiddleSection and passed in as `cards`.

const SCROLL_DURATION_MS = 480
// Ease-out (fast start, gentle settle) for a natural, embla-like feel on click.
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

// Beyond this many px of movement, a pointer gesture counts as a drag (and
// suppresses the card link click that would otherwise fire on release).
const DRAG_THRESHOLD_PX = 8

function GroupCardsRow({ header, cards }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const dragRef = useRef({ active: false, startX: 0, startScroll: 0, moved: false })

  // Distance of one card step (card width + gap), measured on demand at click
  // time (never on mount, so no init reflow). Falls back to ~85% of the viewport.
  const getStep = (el: HTMLDivElement) => {
    const track = el.firstElementChild
    const first = track?.children?.[0] as HTMLElement | undefined
    const second = track?.children?.[1] as HTMLElement | undefined
    if (first && second) {
      return Math.abs(second.offsetLeft - first.offsetLeft)
    }
    if (first) {
      return first.getBoundingClientRect().width
    }
    return el.clientWidth * 0.85
  }

  // Eased scroll to an exact position. Snap is disabled during the tween so the
  // browser doesn't fight the animation, then restored (the target is already a
  // card boundary, so re-enabling snap causes no jump).
  const animateTo = (el: HTMLDivElement, to: number) => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    const start = el.scrollLeft
    const change = to - start
    if (Math.abs(change) < 1) return
    el.style.scrollSnapType = "none"
    const startTime = performance.now()
    const tick = (now: number) => {
      const t = Math.min((now - startTime) / SCROLL_DURATION_MS, 1)
      el.scrollLeft = start + change * easeOutCubic(t)
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        rafRef.current = null
        el.style.scrollSnapType = ""
      }
    }
    rafRef.current = requestAnimationFrame(tick)
  }

  const scrollByStep = (direction: 1 | -1) => {
    const el = scrollRef.current
    if (!el) return
    const step = getStep(el)
    const max = el.scrollWidth - el.clientWidth
    // Align the target to a card boundary (multiple of step) so it lands on a
    // snap point.
    let to = Math.round((el.scrollLeft + direction * step) / step) * step
    to = Math.max(0, Math.min(to, max))
    animateTo(el, to)
  }

  // Mouse drag-to-scroll ("grab and pull") for desktop. Touch is left on native
  // scrolling (the pointerdown bail-out), so momentum scrolling still works there.
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current
    if (!el) return
    if (e.pointerType === "touch") return
    if (e.pointerType === "mouse" && e.button !== 0) return
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startScroll: el.scrollLeft,
      moved: false,
    }
    el.style.scrollSnapType = "none"
    el.style.cursor = "grabbing"
    // NOTE: pointer capture is intentionally NOT taken here. Capturing on
    // pointerdown retargets the ensuing compat mousedown/click to this scroll
    // container, so a plain click never reaches the card <Link> and navigation
    // silently dies. Capture is deferred to onPointerMove, once an actual drag
    // is detected.
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current
    const drag = dragRef.current
    if (!el || !drag.active) return
    const dx = e.clientX - drag.startX
    // First time the gesture crosses the drag threshold, take pointer capture so
    // the drag keeps tracking even if the pointer leaves the element.
    if (!drag.moved && Math.abs(dx) > DRAG_THRESHOLD_PX) {
      drag.moved = true
      try {
        el.setPointerCapture(e.pointerId)
      } catch {
        /* capture unsupported for this pointer */
      }
    }
    el.scrollLeft = drag.startScroll - dx
  }

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current
    const drag = dragRef.current
    if (!el || !drag.active) return
    drag.active = false
    el.style.cursor = ""
    try {
      el.releasePointerCapture(e.pointerId)
    } catch {
      /* pointer already released */
    }
    if (drag.moved) {
      // Ease-settle to the nearest card boundary (animateTo restores snap).
      const step = getStep(el)
      const max = el.scrollWidth - el.clientWidth
      const to = Math.max(0, Math.min(Math.round(el.scrollLeft / step) * step, max))
      animateTo(el, to)
      // animateTo early-returns without restoring snap when already aligned.
      if (Math.abs(to - el.scrollLeft) < 1) el.style.scrollSnapType = ""
    } else {
      el.style.scrollSnapType = ""
    }
  }

  // Block the card <Link> navigation if the gesture was a drag, not a click.
  const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragRef.current.moved) {
      e.preventDefault()
      e.stopPropagation()
      dragRef.current.moved = false
    }
  }

  const renderHeader = () => {
    return (
      <h2
        className={
          "my-0 min-w-0 flex-1 break-words font-bebas text-[28px] font-medium leading-tight tracking-normal text-[#092c3c] min-[600px]:text-[34px]"
        }
      >
        {header}
      </h2>
    )
  }

  const renderPageButtons = () => {
    return (
      <div className="flex shrink-0 flex-row items-center justify-center">
        <div className="flex w-[100px] flex-row justify-between">
          <LeftPageButtonIcon onClick={() => scrollByStep(-1)} />
          <RightPageButtonIcon onClick={() => scrollByStep(1)} />
        </div>
      </div>
    )
  }

  const renderHeaderAndPageButtons = () => {
    return (
      <div className="mb-3 flex w-full flex-row items-center justify-between gap-4">
        {renderHeader()}
        {renderPageButtons()}
      </div>
    )
  }
  // Below 425px we center the card and make it only take up 351px bc that is the size of the card
  return (
    <div className="flex justify-center text-black">
      <div className="flex w-full max-w-[1125px] flex-col items-center justify-center pt-[22px] min-[425px]:block">
        {renderHeaderAndPageButtons()}
        <div
          ref={scrollRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onClickCapture={onClickCapture}
          onDragStart={(e) => e.preventDefault()}
          className="w-[351px] max-w-[calc(100vw-20px)] cursor-grab snap-x snap-mandatory overflow-x-auto pb-[10px] [scrollbar-width:none] min-[425px]:w-full [&::-webkit-scrollbar]:hidden"
        >
          <div className="flex flex-row">{cards}</div>
        </div>
      </div>
    </div>
  )
}

export default GroupCardsRow
